import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import {
  Lightning,
  ShieldCheck,
  CurrencyCircleDollar,
  Bank,
  BatteryCharging,
  GridFour,
  Broadcast,
  Brain,
  Handshake,
  ArrowRight,
  Crosshair,
  GearSix,
  Rocket,
  SealCheck,
  ArrowSquareOut,
} from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/9389779/pexels-photo-9389779.jpeg?auto=compress&cs=tinysrgb&w=1920";

// ── Partner constants (easy to update) ──
const TIANNENG_WEBSITE_URL = "https://www.tiannengglobal.com/";
const TIANNENG_LOGO_SRC = "https://customer-assets.emergentagent.com/job_gjc-latam-hub/artifacts/s7i8gahe_logo-h.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const proofIcons = [Lightning, ShieldCheck, CurrencyCircleDollar, Bank];
const serviceIcons = [BatteryCharging, GridFour, Broadcast, Brain, Handshake];
const processIcons = [Crosshair, GearSix, Rocket];

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-[#0A192F]/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-6"
          >
            {t.hero.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white max-w-4xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.hero.headline}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={1.5}
            variants={fadeUp}
            className="text-lg md:text-xl text-[#0D9488] font-medium mt-4 tracking-wide"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.hero.electronToBit}
          </motion.p>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed"
          >
            {t.hero.subheadline}
          </motion.p>
          <motion.div
            initial="hidden"
            animate="visible"
            custom={3}
            variants={fadeUp}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-talk"
              className="inline-flex items-center justify-center bg-[#0D9488] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0F766E] transition-colors duration-300"
            >
              {t.hero.cta1}
              <ArrowRight size={18} className="ml-2" />
            </Link>
            <Link
              to="/solutions"
              data-testid="hero-cta-solutions"
              className="inline-flex items-center justify-center border border-white/30 text-white px-7 py-3.5 text-sm font-medium hover:bg-white/10 transition-colors duration-300"
            >
              {t.hero.cta2}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== PROOF POINTS ===== */}
      <section data-testid="proof-points-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Core Business Statement */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-20 max-w-3xl"
            data-testid="core-business-block"
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4">
              {t.coreBusiness.overline}
            </p>
            <h2
              className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-5"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.coreBusiness.title}
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-3">
              {t.coreBusiness.text}
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              {t.coreBusiness.details}
            </p>
          </motion.div>

          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] text-center mb-16"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.proofPoints.title}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.proofPoints.items.map((item, i) => {
              const Icon = proofIcons[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-white border border-slate-200 p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  data-testid={`proof-point-${i}`}
                >
                  <Icon size={36} weight="duotone" className="text-[#0D9488] mb-5" />
                  <h3 className="text-lg font-medium text-[#0A192F] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SERVICES OVERVIEW ===== */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.servicesOverview.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16 max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.servicesOverview.title}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.servicesOverview.items.map((item, i) => {
              const Icon = serviceIcons[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-white border border-slate-200 p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  data-testid={`service-card-${i}`}
                >
                  <Icon size={36} weight="duotone" className="text-[#0D9488] mb-5" />
                  <h3 className="text-lg font-medium text-[#0A192F] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={5}
            variants={fadeUp}
            className="mt-12 text-center"
          >
            <Link
              to="/solutions"
              data-testid="services-view-all"
              className="inline-flex items-center text-sm font-medium text-[#0D9488] hover:text-[#0F766E] transition-colors"
            >
              {t.hero.cta2} <ArrowRight size={16} className="ml-1.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== TECHNOLOGY PARTNERS ===== */}
      <section data-testid="partners-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.partners.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16 max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.partners.title}
          </motion.h2>

          {/* Tianneng Partner Card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
            className="border border-slate-200 bg-[#F8FAFC] p-8 md:p-10 max-w-3xl"
            data-testid="partner-tianneng-card"
          >
            <div className="flex flex-col sm:flex-row items-start gap-6 mb-6">
              <a href={TIANNENG_WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <img
                  src={TIANNENG_LOGO_SRC}
                  alt="Tianneng"
                  className="h-14 w-auto object-contain"
                  data-testid="tianneng-logo"
                />
              </a>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <SealCheck size={20} weight="fill" className="text-[#0D9488]" />
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#0D9488]">
                    {t.partners.tianneng.badge}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t.partners.tianneng.description}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-700 font-medium mb-2">
              {t.partners.tianneng.microcopy}
            </p>
            <a
              href={TIANNENG_WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tianneng-website-link"
              className="inline-flex items-center text-sm font-medium text-[#0D9488] hover:text-[#0F766E] transition-colors mb-6"
            >
              {t.partners.tianneng.visitWebsite} <ArrowSquareOut size={16} className="ml-1.5" />
            </a>
            {/* Disclaimer */}
            <div className="border-t border-slate-200 pt-5 mt-2">
              <p className="text-xs text-slate-500 leading-relaxed" data-testid="partner-disclaimer">
                {t.partners.tianneng.disclaimer}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section data-testid="process-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.process.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16 max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.process.title}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.process.steps.map((step, i) => {
              const Icon = processIcons[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="relative"
                  data-testid={`process-step-${i}`}
                >
                  <div className="flex items-center gap-4 mb-5">
                    <span className="text-4xl font-light text-slate-200" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {step.num}
                    </span>
                    <Icon size={32} weight="duotone" className="text-[#0D9488]" />
                  </div>
                  <h3 className="text-xl font-medium text-[#0A192F] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-8 -right-4 w-8">
                      <ArrowRight size={20} className="text-slate-300" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CHILE BLOCK ===== */}
      <section data-testid="chile-section" className="py-24 md:py-32 bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.chileBlock.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-8 max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.chileBlock.title}
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl"
          >
            {t.chileBlock.text}
          </motion.p>
        </div>
      </section>

      {/* ===== LEADERSHIP TEASER ===== */}
      <section data-testid="leadership-section" className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.leadership.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.leadership.title}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.leadership.members.map((member, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-white border border-slate-200 p-8 shadow-sm"
                data-testid={`leader-card-${i}`}
              >
                <div className="w-14 h-14 bg-[#0A192F] text-white flex items-center justify-center text-lg font-medium mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {member.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <h3 className="text-lg font-medium text-[#0A192F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {member.name}
                </h3>
                <p className="text-sm font-medium text-[#0D9488] mb-3">{member.role}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={4}
            variants={fadeUp}
            className="mt-12 text-center"
          >
            <Link
              to="/about"
              data-testid="leadership-cta"
              className="inline-flex items-center text-sm font-medium text-[#0D9488] hover:text-[#0F766E] transition-colors"
            >
              {t.leadership.cta} <ArrowRight size={16} className="ml-1.5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
